/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useEffectOnce } from 'react-use'
import { TSelectOpt } from 'transfix-ui/components/Select/SelectTypes'
import { ICarrierApplication } from 'types/FMSGraphqlTypes'
import { StringParam } from 'use-query-params'
import CarrierScorecardFAQ from 'apps/fleet-planner/components/carrier-scorecard/CarrierScorecardFAQ'
import CarrierScorecardMyRating from 'apps/fleet-planner/components/carrier-scorecard/CarrierScorecardMyRating'
import { StatisticsSection } from 'apps/fleet-planner/components/carrier-scorecard/StatisticsSection'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks/useFMSAnalytics/useFMSAnalytics'
import { Spinner, ISelectProps } from 'transfix-ui'
import { useQueryParams } from 'utils/hooks'
import { useCarrierScorecardInfoQuery } from './graphql/CarrierScorecardInfo'

interface ICarrierScorecardPageParams {
  carrierId?: number
}

const calculateTier = (score: number): string => {
  let tier = ''

  if (score === 5) {
    tier = 'top_tier'
  } else if (score <= 4.99 && score >= 3) {
    tier = 'keep_going'
  } else if (score < 3) {
    tier = 'needs_improvement'
  }

  return tier
}

const getMetric = (rate: number, variant: string): string => {
  if (rate >= 0) {
    if (variant === 'onTimeRate' || variant === 'trackingQuality') {
      if (rate > 95) {
        return 'doingGreat'
      }

      if (rate >= 85 && rate <= 95) {
        return 'almostThere'
      }
      return 'needsImprovement'
    }

    if (variant === 'lateCancellation') {
      if (rate < 5) {
        return 'doingGreat'
      }
      if (rate < 15) {
        return 'almostThere'
      }
      return 'needsImprovement'
    }
  }

  return ''
}

const CarrierScorecardPage = () => {
  const [carrierApplication, setCarrierApplication] = useState<
    Pick<ICarrierApplication, 'carrierScorecard' | 'id' | 'name'>
  >()

  const [{ carrierId }] = useQueryParams<ICarrierScorecardPageParams>({ carrierId: StringParam })

  const { trackEvent } = useFMSAnalytics()
  const { data, loading } = useCarrierScorecardInfoQuery()
  const tier = calculateTier(carrierApplication?.carrierScorecard?.overallScore || 0)

  const carriers: TSelectOpt[] = useMemo(() => {
    return (
      data?.carrierApplicationsForUser?.map(carrier => ({
        value: carrier?.id,
        label: carrier?.name,
      })) || []
    )
  }, [data])

  const setCarrierInfo = useCallback(
    (newCarrierSelected: number = 0) => {
      if (data?.carrierApplicationsForUser) {
        const newScoreData = data?.carrierApplicationsForUser[newCarrierSelected]

        setCarrierApplication(newScoreData)
      }
    },
    [data]
  )

  const onSelectCarrierHandler: ISelectProps['onChange'] = event => {
    const newCarrierSelected = carriers?.findIndex(carrier => carrier?.value === event.target.value)

    setCarrierInfo(newCarrierSelected)
  }

  useEffectOnce(() => {
    trackEvent('Carrier Scorecard Page', 'VIEWED_CARRIER_SCORECARD_PAGE')
  })

  // The request updates data to validate and show the empty state or the carrier scorecard from the Carrier Application
  useEffect(() => {
    const carrierFromParams = carriers.findIndex(carrier => carrier?.value === String(carrierId))
    const carrierDefault = carrierFromParams !== -1 ? carrierFromParams : undefined
    setCarrierInfo(carrierDefault)
  }, [data, carriers, carrierId, setCarrierInfo])

  return (
    <div>
      {loading ? (
        <Spinner position='absolute' />
      ) : (
        <>
          <CarrierScorecardMyRating
            carrierApplication={carrierApplication}
            carriers={carriers}
            onSelectCarrierHandler={onSelectCarrierHandler}
            tier={tier}
            showCarriersSelect={carriers.length > 1}
          />
          <StatisticsSection
            getMetric={getMetric}
            carrierScorecard={carrierApplication?.carrierScorecard || null}
          />
          <CarrierScorecardFAQ />
        </>
      )}
    </div>
  )
}

export default CarrierScorecardPage
